<script setup>
/**
 * Menus — the tappable options your assistant can put under a reply.
 *
 * WAS `pages/MenusPage.vue`, WHICH IS GONE. The nav row went with it: a menu is the front
 * door to a task, the old row said so in its own comment ("an option can START one: the two
 * are built together"), and a row that explains itself by pointing at the row above it is a
 * tab. This is the second tab of `TasksPage`, reached as `…/waslo/tasks?tab=menus`.
 *
 * ── WHAT "MINUS ITS PAGE FRAME" MEANT IN PRACTICE ──────────────────────────────────────
 *
 * The body is byte-for-byte the screen that shipped — same explanation, same example, same
 * rows, same inline empty state, same `data-waslo-*` hooks, so every assertion that was
 * written against it still names the thing it was written about. What is NOT here is
 * `WasloPageLayout`, and with it the four states it rendered. Those moved UP to `TasksPage`,
 * which now drives the frame off whichever tab is open — so a menus read that is still in
 * flight shows the frame's spinner under a tab bar that stays usable, exactly as the
 * period control does on Activity.
 *
 * THIS COMPONENT THEREFORE OWNS NO STATE AT ALL. The read, the on/off write and the failure
 * belong to the page, because the page is what has to answer for them in its header (the
 * primary button follows the tab) and in its frame. One owner, one answer.
 */
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import MenuCard from './MenuCard.vue';
import MenuExample from './MenuExample.vue';
import WasloExplainer from './WasloExplainer.vue';
import { useWasloI18n } from '../i18n.js';

defineProps({
  /** Rows from `wasloApi.getMenus`, already read by the page. */
  rows: { type: Array, default: () => [] },
  accountId: { type: Number, default: null },
  /** The id whose switch is being written, or null. */
  savingId: { type: [Number, String], default: null },
  /** Bumped by the page after a failed write, to snap `Switch` back (see `TaskCard`). */
  switchEpoch: { type: Number, default: 0 },
  /** One sentence for the whole list when a switch could not be saved. */
  saveError: { type: String, default: '' },
  /** E2 (P-8.4): the menus that still carry "Suggested by Claude". */
  claudeDrafted: { type: Array, default: () => [] },
});

const emit = defineEmits(['toggle', 'add']);

const { t } = useWasloI18n();
</script>

<template>
  <div class="flex flex-col gap-8 pb-10" data-waslo-menus-tab>
    <div class="grid gap-4 lg:grid-cols-2">
      <WasloExplainer
        icon="i-lucide-list"
        :title="t('WASLO.MENUS.SPOTLIGHT_TITLE')"
        :note="t('WASLO.MENUS.SPOTLIGHT')"
      />
      <MenuExample />
    </div>

    <section class="flex flex-col gap-4" data-waslo-menus>
      <SettingsHeader
        :heading="t('WASLO.MENUS.LIST.HEADING')"
        :description="t('WASLO.MENUS.LIST.DESCRIPTION')"
      />

      <Banner v-if="saveError" color="ruby" data-waslo-menus-error>
        {{ saveError }}
      </Banner>

      <ul
        v-if="rows.length"
        class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
        data-waslo-menu-list
      >
        <MenuCard
          v-for="menu in rows"
          :key="menu.id"
          :menu="menu"
          :account-id="accountId"
          :is-saving="savingId === menu.id"
          :switch-epoch="switchEpoch"
          :suggested-by-claude="claudeDrafted.includes(menu.id)"
          @toggle="next => emit('toggle', menu, next)"
        />
      </ul>

      <div
        v-else
        class="flex items-start w-full gap-4 px-6 py-6 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
        data-waslo-menus-empty
      >
        <span
          class="grid shrink-0 place-items-center size-9 rounded-xl bg-n-blue-3"
          aria-hidden="true"
        >
          <span class="size-5 i-lucide-list-plus text-n-blue-10" />
        </span>
        <div class="flex flex-col items-start gap-3">
          <div class="flex flex-col gap-1">
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{ t('WASLO.MENUS.EMPTY.TITLE') }}
            </p>
            <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
              {{ t('WASLO.MENUS.EMPTY.BODY') }}
            </p>
          </div>
          <Button
            :label="t('WASLO.MENUS.EMPTY.ACTION')"
            icon="i-lucide-plus"
            size="sm"
            data-waslo-menu-add-first
            @click="emit('add')"
          />
        </div>
      </div>
    </section>
  </div>
</template>
